import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import OrderForm from '../components/OrderForm';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost, API_BASE } from '../utils/api';
import {
  fetchProductionsOrThrow,
  fetchSuppliersOrThrow,
  fetchSupplierSuggestionsOrThrow,
  fetchSubDepartments,
  fetchGagsOrEmpty,
  loadAccountingLists,
} from '../utils/orderReferenceLists';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import { notify as notifyDataBus } from '../utils/dataBus';
import { BTN } from '../constants/buttons';
import { getOrderNumber } from '../utils/orderTitle';
import usePendingOrderUploads from '../hooks/usePendingOrderUploads';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import { useLatestRequest } from '../hooks/useSearchRequest';
import ConfirmDialog from '../components/ConfirmDialog';
import { isRealAssignment } from '../utils/personalSpace';
import { collidingDepartmentIds } from '../components/ProductionDepartmentSwitcher';
import { withIdSuffix } from '../utils/duplicateLabels';

// Local storage keys for remembered fields
const LS_KEYS = {
  requestedBy: 'sfx_requested_by',
  department: 'sfx_department',
  buyerEmail: 'sfx_buyer_email',
  userPhone: 'sfx_user_phone',
};

function NewOrder() {
  const location = useLocation();
  // State, not a const off `location.state` (2026-08-30): a confirmed
  // department switch RESETS the form (see the switch-reset effect below),
  // and the "Duplicating from ORD-NNN" banner must not outlive the prefill
  // it describes — the duplicated content belonged to the OLD department.
  const [duplicateOrder, setDuplicateOrder] = useState(location.state?.duplicateFrom || null);

  // 🔻 `isAllDepartments` LEFT this destructure on 2026-08-31 with the All
  // Productions mode and the create-needs-a-department prompt it gated.
  // `clientAuthorityManifest.test.js` pins this line's exact source text
  // (`pages/NewOrder.js` entry, PLUMBING verdict) — its declaration string was
  // updated in the same commit as this one.
  const { assignments, activeDepartment, isAdmin, capabilities, loading: deptLoading, registerDirtyEditGuard } = useDepartment();
  // The cost-code and payment-type feeds answer [] to a member without
  // canViewBudget IN THIS DEPARTMENT (routes/budget.js, both /active routes).
  // Mirror that per-department capability; never the unscoped buyer flag,
  // which left a department-route buyer with empty pickers (LR, 2026-09-24).
  const canLoadAccountingPickers = isAdmin || capabilities.canViewBudget === true;
  const { user } = useAuth();

  // Data loaded once and passed to OrderForm
  const [productions, setProductions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [gags, setGags] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [userAssignedProductions, setUserAssignedProductions] = useState(null);
  // 🚨 WHICH OF THE ARRAYS ABOVE ARE EMPTY BECAUSE THE REQUEST FAILED — 2026-08-22.
  //
  // `OrderForm` renders confident sentences off these arrays and cannot see
  // the requests that filled them: *"No suppliers set up for this department
  // yet"* is worded to send the user off to CREATE supplier records, and an
  // emptied cost-code list silently redraws every coded line as `-- None --`.
  // Both are false when the fetch was merely refused, and THIS is the order-
  // CREATION path — the one where acting on the supplier sentence produces a
  // duplicate `suppliers` row, a measured live-production hazard on this
  // instance. See the `loadFailures` prop in `components/OrderForm.js`.
  //
  // 🪤 TWO RULES, AND BOTH ARE ABOUT THE SAME RACE. These are independent
  // requests resolving in any order, and the supplier picker is fed by TWO of
  // them (records + past-order names).
  //   1. Every write is the functional `setLoadFailures(prev => …)` form, never
  //      a wholesale `setLoadFailures({ suppliers: true })`, which would erase
  //      a failure another list recorded a tick earlier.
  //   2. A flag is only ever set TRUE here, never cleared to `false` on
  //      success. Clearing is what re-opens the race: the records call
  //      succeeding AFTER the suggestions call failed would write `false` over
  //      a real failure, and the picker would go back to claiming the
  //      department has no suppliers. The RESET happens once, at the top of
  //      `loadFormData`, so a retry starts from a clean slate.
  const [loadFailures, setLoadFailures] = useState({});

  // Effective initial order — duplicate-from on first mount, plus the small
  // wrapper-owned overrides (remembered fields, auto-selected production,
  // department auto-fill). Bumping the key on this object forces OrderForm
  // to re-mount with the new defaults (e.g. after a successful submission
  // or "Create Another").
  const [initialOrder, setInitialOrder] = useState(duplicateOrder);
  const [formKey, setFormKey] = useState(0);

  // 🐛→✅ FLAKY-C1 FIX (2026-08-18). `formKey` is the ONLY channel these
  // effects have to push a late-arriving default (active department, default
  // sub-department, remembered fields) into `OrderForm` — that component
  // deliberately ignores `initialOrder` prop changes in create mode (see its
  // own comment at the re-hydrate effect: "Skip during create-mode to avoid
  // clobbering localStorage hydration"), so a `key` bump is the only way to
  // apply a default once it has loaded. Every one of those loads is a network
  // round trip (`GET /api/roles/my-assignments`, `GET /api/sub-departments`),
  // so it can resolve at ANY time after mount — including after the user has
  // already started typing. When it does, the bump REMOUNTS `OrderForm`,
  // which re-initialises `items` to a single blank, non-hire line
  // (`buildInitialItems` → `[createEmptyItem()]`, since `initialOrder` here
  // never carries `items`) — silently discarding everything typed so far.
  // This is a REAL user-facing defect, not just a test artefact: on a slow
  // connection a user who starts filling the form before the department/
  // sub-department fetch resolves gets their input wiped with no warning.
  //
  // `isDirty` from `OrderForm` (via `onDirtyChange`) is the exact predicate
  // for "has the user started editing" — a stronger, existing concept that
  // is not "has any effect fired yet". Gate every `setFormKey` bump on it
  // being false: apply defaults freely while the form is still pristine,
  // stop clobbering the moment the user has touched anything. The
  // `setInitialOrder` calls stay unconditional — harmless, since a skipped
  // remount means nothing re-reads them until a genuine future remount
  // (e.g. "Create Another"), at which point the latest values are correct.
  const [formDirty, setFormDirty] = useState(false);
  const formDirtyRef = useRef(false);
  useEffect(() => { formDirtyRef.current = formDirty; }, [formDirty]);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  // One attempt-identity for `POST /api/orders` — see the header comment at
  // the apiPost call in `submitOrder`.
  const createIdem = useIdempotencyKey();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedRef, setSubmittedRef] = useState('');
  const [submittedId, setSubmittedId] = useState(null);
  const [submitError, setSubmitError] = useState('');
  // PARTIAL outcome: the order EXISTS but one or more attachment uploads did
  // not land — `{ orderId, orderRef, failedNames }`, rendered as the amber
  // panel above the form. NEVER `submitError` for this state: "Error
  // submitting order" over an order that exists is what sends the user back
  // to the Submit button believing nothing was created — the first half of
  // the duplicate-order defect this page shipped (W3, 2026-08-27).
  const [attachmentWarning, setAttachmentWarning] = useState(null);
  const [loadError, setLoadError] = useState('');

  // 🔻 THE ALL-MODE "choose a department" PROMPT WAS REMOVED ON 2026-08-31
  // with the All Productions mode. It existed because that mode made
  // `activeDepartment.department_id` a sentinel string rather than a
  // department, so a validated, already-submitted order had nowhere to go:
  // `handleSubmit` deferred the attempt (`pendingAllModeSubmit`), asked for a
  // real department, and scoped the three department-fetch effects to the
  // answer (`effectiveDeptIdForLists` / `allModeResolvedDeptId`). With one
  // department active at a time, `activeDepartment.department_id` is always a
  // real id and all of that is unreachable.
  //
  // 🔑 THE DEPARTMENT-SWITCH DIRTY-EDIT DIALOG IS A DIFFERENT DIALOG AND IS
  // UNTOUCHED. That one asks "you are about to LOSE typed work by switching —
  // proceed?" and hangs off `registerDirtyEditGuard`/`hasUnsavedWork`; this
  // one asked "WHERE does this order go?" after Submit. They were kept
  // separate deliberately and only the second is gone.
  //
  // 🟢 `submitOrder(payload, items, departmentId)` KEEPS its third parameter
  // and its whole body: that is the idempotency / attempt-identity machinery
  // reworked on 2026-08-29 and pinned by two other suites. Only the caller
  // changed — `handleSubmit` now passes the active department straight
  // through, as it did before the mode.

  // THE ATTEMPT'S UNFINISHED HALF — the attachment CLAIMS still OWED to an
  // order whose create has been CONFIRMED (2xx parsed). (Uploads until
  // 2026-09-15; the bytes now go up at pick, R13.) Attempt-scoped like the
  // idempotency key itself, and the reason a resubmit cannot duplicate:
  //  - while this ref holds an attempt, `submitOrder` SKIPS `POST /api/orders`
  //    entirely and runs only the remaining claims. A create that is never
  //    re-sent cannot mint a second order — a STRONGER guarantee than
  //    relying on the server replaying the retained key, because the stored
  //    replay lasts only until the 24h idempotency TTL sweep
  //    (`server/helpers/dailyHealthCheck.js`), while this holds for the
  //    whole life of the panel.
  //  - claim lines are PRUNED as they attach, so a retry claims only what is
  //    missing — an attached file is never claimed twice.
  //  - the line list is planned ONCE per confirmed order, from the form state
  //    at that moment; the form stays visible (md+) in the partial state,
  //    so re-deriving on retry against possibly-edited items could attach a
  //    file to the wrong line.
  // The key itself stays retained until everything lands (cleared with this
  // ref in the full-success branch) — so even the paths that DO re-send the
  // create (a create failure, a 2xx whose body failed to parse) are deduped
  // server-side under the same key.
  const pendingUploadsRef = useRef(null);

  // Attachments (2026-09-15, R13): uploaded AT PICK to the caller's pending
  // store and CLAIMED onto the order once it exists (owner: *"upload the file
  // straight away, delete it if the user does not save the line or they delete
  // the line"*). OrderForm renders the rows; the hook owns the uploads, and
  // leaving this page without submitting discards them.
  const pending = usePendingOrderUploads();

  // ---- The department-switch dirty guard (2026-08-30) ----
  //
  // The live bug: the owner half-filled an order, switched department in the
  // header, and the form silently stayed on screen — while the submit path
  // reads `department_id` from `activeDepartment` AT SUBMIT TIME, so
  // the order would have been raised in the NEW department (its budget, its
  // approval chain) carrying selections made under the old one. Ruling:
  // *"yes fix it with the warning 'You have unsaved changes — they'll be
  // lost.' I dont mind if the users looses their work when they switch as
  // long as they agree to the warning."*
  //
  // This page is the FOURTH registrant of `DepartmentContext`'s
  // `registerDirtyEditGuard` — same ref, same `ProductionDepartmentSwitcher`
  // dialog, same wording; no second mechanism. (The context docblock's
  // three-page enumeration predates this registration.)
  //
  // 🔑 WHAT COUNTS AS DIRTY, AND WHY EACH TERM:
  //  · `formDirty` is OrderForm's own hydration-snapshot predicate — it reads
  //    `true` only when `{ form, items }` differ from what the form HYDRATED
  //    with, so seeded defaults (remembered fields, default lead time, the
  //    auto blank line, a duplicate-from prefill) can never warn. "Dirty"
  //    really is "the user changed something".
  //  · `&& !submitSuccess` — after a FULL success the typed content IS the
  //    saved order; the snapshot still reads dirty (the form is never
  //    re-hydrated on success) but there is nothing left to lose, and a
  //    warning here would be a false positive. The PARTIAL outcome
  //    (`attachmentWarning`) deliberately still warns: conservative, and
  //    `submitSuccess` is never true there.
  //  · the file term — pending uploads live in `pending`, not in OrderForm's
  //    `{ form, items }`, so the snapshot CANNOT see them. Without it a user
  //    whose entire contribution is an uploaded file would lose it with no
  //    warning at all.
  const hasUnsavedWork = (formDirty && !submitSuccess) || pending.hasEntries;
  useEffect(() => {
    // Optional-chained like the other three registrants: unit harnesses mock
    // the context without the guard, and a page must not require it.
    registerDirtyEditGuard?.(hasUnsavedWork);
    return () => registerDirtyEditGuard?.(false);
  }, [hasUnsavedWork, registerDirtyEditGuard]);

  // ---- Leaving the page with unsaved work (2026-09-15, R14) ----
  // Owner: *"when I start filling out an order and switch to a differnt page
  // the order is lost"*. The SAME predicate as the department-switch guard
  // above — one definition of "there is work to lose" for both exits. They
  // cannot both fire for one action: a department switch does not navigate,
  // and the notification bell (`Layout.js`) switches only AFTER its navigation
  // is allowed. Leaving unmounts `usePendingOrderUploads`, which discards the
  // picked files on the server — which is what "Leave and discard" says.
  // 🔑 No post-save bypass is needed here: a submit never navigates; the
  // success panel's links render after `submitSuccess` has cleared the flag.
  const leaveGuard = useUnsavedChangesGuard({
    when: hasUnsavedWork,
    message: attachmentWarning && !submitSuccess
      ? `Order ${attachmentWarning.orderRef} was created, but some attachments haven't been attached. If you leave, they will be lost.`
      : `This order hasn't been submitted. If you leave, your changes${pending.hasEntries ? ' and attached files' : ''} will be lost.`,
  });

  // ---- Reset the form when a switch actually happens ----
  //
  // By the time `activeDepartment.department_id` CHANGES here, the switch is
  // legitimate: either the form was clean, or the user pressed Continue on
  // the dialog and accepted the loss (Cancel never reaches
  // `switchDepartment`, so this effect cannot fire from a Cancel).
  //
  // RESET, not refetch, is the chosen repair for the half-migrated state:
  // the option LISTS (sub-departments, cost codes, payment types) already
  // refetch — their effects below are keyed on `activeDepartment` — but the
  // form's SELECTED values do not follow, because every default-driven
  // `formKey` bump is gated on `!formDirtyRef.current`. Re-hydrating a dirty
  // form selectively would mean deciding, field by field, which old-
  // department choices to keep — and the owner has already accepted that the
  // work is lost. So: keep the PERSON-scoped remembered fields
  // (requested_by / buyer_email / user_phone — facts about the user, stable
  // across departments, the same distinction the remembered-fields
  // convention draws), stamp the new department's name, and drop everything
  // department-scoped: `sub_department` (the effect below re-defaults it for
  // the new department), the duplicate-from prefill, and every pending upload
  // — discarded on the server, since the lines they belonged to are gone.
  //
  // ⚠️ Deliberately NOT touched: `pendingUploadsRef`, `createIdem`,
  // `attachmentWarning`, `submitSuccess` — the attempt identity
  // (2026-08-29/30). In the PARTIAL state the owed CLAIMS live in
  // `pendingUploadsRef` (a ref; a remount cannot clear it) and their files are
  // NOT discarded here, so the amber panel's "press Submit to retry, nothing
  // will be duplicated" promise survives this reset unchanged.
  //
  // 🪤 Compared by ID, not object identity — the provider re-resolves the
  // assignment object on refetch, and a fresh object for the SAME department
  // must not wipe the form.
  const lastSwitchDeptIdRef = useRef(null);
  useEffect(() => {
    if (deptLoading || !activeDepartment) return;
    const deptId = activeDepartment.department_id;
    const prevId = lastSwitchDeptIdRef.current;
    lastSwitchDeptIdRef.current = deptId;
    if (prevId === null || prevId === deptId) return; // first resolve, or no real switch

    setDuplicateOrder(null);
    if (!pendingUploadsRef.current) pending.discardAll();
    setInitialOrder(prev => {
      const current = prev || {};
      return {
        requested_by: current.requested_by,
        buyer_email: current.buyer_email,
        user_phone: current.user_phone,
        department: activeDepartment.department_name || '',
      };
    });
    // Unconditional — unlike the default-arrival bumps this is not racing
    // the user's typing; it IS the accepted consequence of the switch.
    setFormKey(k => k + 1);
  }, [activeDepartment, deptLoading]);

  // ---- Load remembered fields from localStorage on mount ----
  useEffect(() => {
    const remembered = {};
    if (localStorage.getItem(LS_KEYS.requestedBy)) remembered.requested_by = localStorage.getItem(LS_KEYS.requestedBy);
    if (localStorage.getItem(LS_KEYS.department))  remembered.department    = localStorage.getItem(LS_KEYS.department);
    if (localStorage.getItem(LS_KEYS.buyerEmail))  remembered.buyer_email   = localStorage.getItem(LS_KEYS.buyerEmail);
    if (localStorage.getItem(LS_KEYS.userPhone))   remembered.user_phone    = localStorage.getItem(LS_KEYS.userPhone);
    if (Object.keys(remembered).length > 0) {
      setInitialOrder(prev => ({ ...(prev || {}), ...remembered }));
      if (!formDirtyRef.current) setFormKey(k => k + 1);
    }
  }, []);

  // Fallback: seed requested_by from auth user if localStorage hasn't
  // hydrated it (fresh signup, or mount races AuthContext).
  useEffect(() => {
    if (!user?.display_name) return;
    setInitialOrder(prev => {
      const current = prev || {};
      if (current.requested_by) return prev;
      return { ...current, requested_by: user.display_name };
    });
  }, [user?.display_name]);

  // ═══════════════════════════════════════════════════════════════════════
  // MY SPACE "HEADER DETAILS" — the user's saved DEFAULT (2026-09-11)
  // ═══════════════════════════════════════════════════════════════════════
  // Owner: *"Once text has been entered this should persist for ALL MySpace
  // orders and update on new orders when the user changes the text."* So a new
  // order OPENS on whatever the user last saved, and `OrderForm` takes it the
  // way it takes every other remembered create-path field — through
  // `initialOrder`, so it lands in form state and is editable, rather than as a
  // prop the form would have to special-case.
  //
  // 🪤 `GET /api/users/me`, NOT `useAuth().user`. This page already holds a
  // current user, from `GET /api/auth/me` via `AuthContext` — and that payload
  // does NOT carry this column and deliberately must not: it is read app-wide
  // on every page load, and this is up to 2,000 characters of free text nothing
  // else needs. `/api/users/me` is the PERSONAL-scoped route that owns the
  // caller's own profile.
  //
  // 🔴 FAILS SILENT, AND THAT IS THE RIGHT FAILURE. A refused request leaves the
  // box empty with its placeholder showing, which is exactly the state of a user
  // who has never saved one — so the worst case is retyping, never a false claim
  // and never a blocked order. It is deliberately NOT in `loadFailures`: that
  // machinery exists because an empty ARRAY there makes `OrderForm` assert
  // something untrue ("no suppliers set up"), and an empty box asserts nothing.
  //
  // 🔑 CALLED FROM `loadFormData`, so it re-runs on every mount and on the
  // explicit retry — which is what makes the NEXT new order open on the value
  // the previous one just saved, with no reload. "Create Another" does not
  // remount the page, so `handleCreateAnother` re-fetches it explicitly below.
  const loadHeaderDetailsDefault = () => {
    apiGet(`${API_BASE}/api/users/me`)
      .then(res => (res.ok ? res.json() : null))
      .then(me => {
        if (!me || typeof me.myspace_header_details !== 'string') return;
        setInitialOrder(prev => {
          const current = prev || {};
          // Never clobber something the user has already typed in this session.
          if (current.header_details) return prev;
          return { ...current, header_details: me.myspace_header_details };
        });
        // Same dirty guard every other late-arriving default on this page uses
        // (see `formDirtyRef` above): applying a default through a `key` bump
        // remounts `OrderForm` and would discard anything already typed.
        if (!formDirtyRef.current) setFormKey(k => k + 1);
      })
      .catch(() => { /* silent — see above */ });
  };

  // ---- Load productions + suppliers ----
  const loadFormData = () => {
    setLoadError('');
    loadHeaderDetailsDefault();
    // The one place failures are cleared — see rule 2 at `loadFailures`.
    setLoadFailures({});
    // 🔑 THIS PAGE IS THE ONE THAT REPORTS A PRODUCTIONS FAILURE AS A VISIBLE
    // `loadError`; the two expansion hooks degrade to `[]` with no flag at all.
    // That is why `utils/orderReferenceLists.js` exposes an `…OrThrow` / an
    // `…OrEmpty` pair rather than one fetcher: the module owns what a failure
    // IS, the caller owns what it MEANS to its screen.
    fetchProductionsOrThrow()
      .then(data => setProductions(data))
      .catch(err => setLoadError(friendlyErrorMessage(err)));

    // 🔴 THE COMMENT THAT USED TO END THIS CALL — `// suppliers optional
    // (autocomplete)` — WAS TRUE OF THE FIELD AND FALSE OF THE SCREEN, and
    // that gap is the whole defect. The field really is free text and a failed
    // fetch really does not block the order. But the empty array it leaves
    // behind makes `OrderForm` state, in its own voice, that this department
    // has no suppliers set up — wording deliberately chosen to send the reader
    // to Admin Settings to create some. "Optional" described the data
    // dependency and licensed a silent failure that produces a false claim and
    // an invitation to duplicate live supplier records.
    // 🪤 A NON-OK RESPONSE MUST TAKE THE SAME PATH AS A REJECTED FETCH. The
    // `throw` inside `fetchSuppliersOrThrow` is what routes a 500 into this
    // catch; without it a refused request would set the flag on one branch and
    // not the other.
    fetchSuppliersOrThrow()
      .then(data => setSuppliers(data))
      .catch(() => setLoadFailures(prev => ({ ...prev, suppliers: true })));

    // Supplier names typed on PAST APPROVED ORDERS. A SECOND tier beside the
    // curated `suppliers` list, never merged into it server-side — see
    // `utils/supplierOptions.js`. Optional in exactly the same way: on failure
    // the box falls back to the records alone and the field stays free text.
    // 🔑 IT REPORTS UNDER THE SAME `suppliers` KEY AS THE RECORDS ABOVE, AND
    // THAT IS DELIBERATE: the two tiers are merged into ONE picker
    // (`utils/supplierOptions.js`), so a reader cannot be told which half is
    // missing in any useful way — only that the list in front of them is
    // incomplete. Splitting the key would put a distinction on screen that
    // means nothing to the person typing a supplier name.
    fetchSupplierSuggestionsOrThrow()
      .then(data => setSupplierSuggestions(Array.isArray(data) ? data : []))
      .catch(() => setLoadFailures(prev => ({ ...prev, suppliers: true })));

    // Gag names for the Job / Rig / Gag autocomplete (department-scoped, full
    // transparency). Optional: on any failure the field just stays free-text.
    fetchGagsOrEmpty()
      .then(data => setGags(Array.isArray(data?.gags) ? data.gags : []))
      .catch(() => {});
  };

  useEffect(() => {
    loadFormData();
  }, []);

  // ---- Load sub-departments scoped to active department ----
  useEffect(() => {
    if (deptLoading) return;
    const deptId = activeDepartment ? activeDepartment.department_id : null;
    // 🪤 THIS IS THE ONE SURFACE THAT NARROWS sub-departments to a department;
    // the other three ask unscoped and let the server apply the caller's own
    // auto-scope. `fetchSubDepartments` takes the id as an OPTIONAL argument
    // for exactly that reason — do not start passing one elsewhere.
    fetchSubDepartments(deptId)
      .then(data => {
        setSubDepartments(data);
        // Default sub-department on first load (not during duplicate)
        if (!duplicateOrder) {
          // 1. The caller's OWN seat in this department (owner 2026-10-08:
          // "pre fill, keeping the option to select another") — Marco in
          // Workshop opened on "-- Select --". `sub_department_id` rides on
          // the assignment from `GET /api/roles/my-assignments`. 2. The My
          // Settings preference (one value for every department, so second).
          // 3. "Default". The select stays fully editable either way.
          const seatSubId = activeDepartment && activeDepartment.sub_department_id;
          const seatSub = seatSubId ? data.find(sd => sd.id === seatSubId) : null;
          const userSubDept = localStorage.getItem('sfx_sub_department');
          const candidate = (seatSub && seatSub.name) || userSubDept || 'Default';
          const exists = data.some(sd => sd.name === candidate);
          const fallback = exists ? candidate : (data.some(sd => sd.name === 'Default') ? 'Default' : null);
          if (fallback) {
            setInitialOrder(prev => {
              const current = prev || {};
              if (current.sub_department) return prev;
              return { ...current, sub_department: fallback };
            });
            if (!formDirtyRef.current) setFormKey(k => k + 1);
          }
        }
      })
      .catch(() => {});
  }, [activeDepartment, deptLoading, duplicateOrder]);

  // ---- Load cost codes (admin/buyer only) ----
  // 🔑 Sequenced (R14 review M2, same fix as `OrderPage.js`): a department
  // switch refetches, and the OLD department's list must not win by resolving
  // second — an order raised in SFX offering Costume's cost codes.
  const beginCostCodes = useLatestRequest();
  const beginPaymentTypes = useLatestRequest();
  useEffect(() => {
    // 🪤 BOTH SEQUENCES ARE BEGUN BEFORE THE GATE, exactly as the two separate
    // effects did — `begin()` was called above their early returns too, so the
    // sequence advances on a gated pass and a request already in flight for the
    // old department can no longer win.
    const costCodesCurrent = beginCostCodes();
    const paymentTypesCurrent = beginPaymentTypes();
    // 🪤 THE GATE ALSO CLEARS THE FLAGS, AND IT MUST. Switching to a department
    // the viewer cannot see accounting for empties the lists legitimately;
    // carrying a stale failure across that switch would print "couldn't be
    // loaded" over lists that were never meant to be fetched. Passing a null
    // `departmentId` is how that branch is expressed to the shared loader.
    // 🔑 `isCurrent` IS THIS PAGE'S ALONE, PER LIST, and is an OPTIONAL argument
    // — the other three surfaces pass none, which is exactly what they did
    // before the extraction. It is a `useLatestRequest` sequencing guard (R14
    // review M2): a department switch refetches, and the OLD department's list
    // must not win by resolving second, or an order raised in SFX offers
    // Costume's cost codes. 🚫 Neither add one elsewhere nor remove this one
    // without an owner ruling — both are behaviour changes.
    // 🔑 TWO sequences, not one: the two lists are two requests, either
    // refusable alone and so either staleable alone.
    loadAccountingLists({
      departmentId: (deptLoading || !activeDepartment || !canLoadAccountingPickers)
        ? null
        : activeDepartment.department_id,
      entries: [
        { setList: setCostCodes, failureKey: 'costCodes', isCurrent: costCodesCurrent },
        { setList: setPaymentTypes, failureKey: 'paymentTypes', isCurrent: paymentTypesCurrent },
      ],
      setLoadFailures,
    });
  }, [activeDepartment, deptLoading, canLoadAccountingPickers, beginCostCodes, beginPaymentTypes]);

  // ---- Derive userAssignedProductions ----
  //
  // 🔻 THE AUTO-PICK IS GONE (2026-08-12), AND DELETING IT IS PART OF THE FIX
  // RATHER THAN A TIDY-UP. It seeded `initialOrder.production_id` with the
  // user's FIRST non-personal assigned production, which is not necessarily the
  // production of the department the order actually files into — and since
  // the submit path sends `department_id: activeDepartment.department_id`,
  // and `POST /api/orders` derives `production_id` from THAT department, the
  // seeded value could disagree with where the order landed. That disagreement
  // is the defect this change closes; leaving the seed would keep a second,
  // silently-wrong answer alive in form state for someone to render again later.
  //
  // 🔑 `OrderForm` now DERIVES the read-only Production display from the live
  // `useDepartment().activeDepartment`, so nothing needs seeding and the field
  // follows the header switcher instead of a value frozen at mount.
  //
  // ⚠️ `userAssignedProductions` IS STILL NEEDED and is why this effect survives:
  // `OrderForm` reads it for `hasNoAssignments`, which gates the Sub Department
  // select's `Not Assigned` option. `productions` therefore comes off the
  // dependency list — it was only ever read by the auto-pick.
  useEffect(() => {
    if (deptLoading) return;

    if (isAdmin) {
      setUserAssignedProductions(null); // admin sees all
      return;
    }

    if (assignments.length === 0) {
      setUserAssignedProductions([]);
      return;
    }

    const uniqueProds = [];
    const seenIds = new Set();
    assignments.forEach(a => {
      if (!seenIds.has(a.production_id)) {
        seenIds.add(a.production_id);
        uniqueProds.push({ production_id: a.production_id, production_name: a.production_name, company_name: a.company_name });
      }
    });
    setUserAssignedProductions(uniqueProds);
  }, [assignments, isAdmin, deptLoading]);

  // ---- Auto-populate department field from active department ----
  useEffect(() => {
    if (deptLoading || !activeDepartment) return;
    setInitialOrder(prev => {
      const current = prev || {};
      return { ...current, department: activeDepartment.department_name || '' };
    });
    if (!formDirtyRef.current) setFormKey(k => k + 1);
  }, [activeDepartment, deptLoading]);

  // ---- Attachments ----
  // 🔀 2026-09-15 (R13): the files are ALREADY on the server (uploaded at pick),
  // so what an order is owed after its create is a CLAIM per line, not an
  // upload. `usePendingOrderUploads#claimPlanned` attempts every line even after
  // one fails and never throws; each failure carries the `line` a retry passes
  // back (a new line's id resolved by its echoed `client_key`, never by position),
  // so a retry claims only what is still owed and never re-sends the create.

  // ---- Submit ----
  // SUCCESS = the order exists AND every attachment attached; only then is the
  // idempotency key cleared. Anything less is either a create failure (key
  // retained, red error, resubmit deduped by the same key) or a PARTIAL (the
  // order is real, `pendingUploadsRef` holds what is owed, the amber panel
  // reports it honestly, and a resubmit finishes the uploads WITHOUT
  // re-sending the create). The UI must never report "failed" for an order
  // that exists — that report is the step that produced duplicate orders.
  // The real submission — unchanged from the pre-2026-08-31 `handleSubmit`
  // except that the department id is now a PARAMETER rather than read
  // straight off `activeDepartment`, so the All-mode gate below can supply
  // the department the user picked in the dialog instead of the sentinel.
  const submitOrder = async (payload, items, departmentId) => {
    setSubmitError('');
    setSubmitSuccess(false);
    setAttachmentWarning(null);
    setSubmitting(true);

    try {
      // A resubmit while uploads are owed to an ALREADY-CONFIRMED order:
      // skip the create entirely — see `pendingUploadsRef`'s comment.
      let attempt = pendingUploadsRef.current;

      if (!attempt) {
        // The files must be able to go with this order BEFORE it is created:
        // none still uploading or refused, none on a line `buildPayload` drops.
        const planned = pending.planClaims(items, payload && payload.items);
        if (planned.message) {
          setSubmitError(planned.message);
          return;
        }
        // Tag department for new orders. Server uses x-department-id header
        // primarily but the payload is the canonical source on create.
        const fullPayload = {
          ...payload,
          // 🔻 `personal_ref: payload.personal_ref || ''` came off on 2026-08-13
          // with the order-level Personal Ref field (OrderForm no longer holds it
          // in form state, so `payload` never carries it). The server stores NULL
          // for an absent value on create; the DB column stays.
          department_id: departmentId,
        };

        // `Idempotency-Key`: one identity per create ATTEMPT — minted here (the
        // guard in OrderForm has just armed), REUSED if this attempt fails and
        // the user retries, cleared only when the WHOLE attempt — create AND
        // attachment uploads — has succeeded. OrderForm's ref guard closes the
        // one-tab double-click; this header is what lets the server dedupe the
        // cases the client cannot see (a network-layer retry, a resend after a
        // lost response). Optional server-side, so it is safe to send ahead of
        // the server honouring it.
        const res = await apiPost(`${API_BASE}/api/orders`, fullPayload, {
          headers: { 'Idempotency-Key': createIdem.mint() },
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to submit order');
        }
        // ⚠️ Parse BEFORE recording the order as confirmed and long before the
        // key is cleared. `clear()` used to run before this parse — so a 2xx
        // whose body was lost mid-stream reported "failed" with a FRESH key
        // armed for the resubmit: the same duplicate hazard as the upload one,
        // through a different door. A parse throw now lands in the outer catch
        // with the key retained, and the retry replays server-side.
        const order = await res.json();

        attempt = {
          orderId: order.id,
          // The order NUMBER (2026-08-12) — the string the panels show and
          // the one the user quotes to their buyer.
          orderRef: getOrderNumber(order),
          lines: planned.lines,
          savedItems: order.items,
        };
        pendingUploadsRef.current = attempt;

        // The order landed — tell any open Budget Overview / Hires tab now,
        // not only on full success: cost-to-date changed the moment the row
        // was created, attachments or not.
        notifyDataBus('budget-changed', {
          departmentId: fullPayload.department_id,
          reason: 'create',
        });
        notifyDataBus('hires-changed', {
          departmentId: fullPayload.department_id,
          reason: 'order-create',
        });
      }

      // ── From here the order EXISTS. Nothing below may surface through the
      // outer catch as a submit failure; an upload problem is reported as an
      // attachment problem, against the order's own number.
      let failedNames;
      try {
        const failures = await pending.claimPlanned(attempt.orderId, attempt.lines, attempt.savedItems);
        attempt.lines = failures.map((f) => f.line);
        failedNames = failures.flatMap((f) => f.names);
      } catch {
        // Belt over braces — claimPlanned never throws, so this is unreachable
        // in ordinary operation; if something unforeseen throws anyway it is by
        // construction an attachment problem, never a submit failure.
        failedNames = ['attachments'];
      }

      if (failedNames.length > 0) {
        // PARTIAL: keep the key, keep the owed tasks, say exactly what
        // happened. The form stays visible below the panel; Submit retries
        // only the missing uploads.
        setAttachmentWarning({
          orderId: attempt.orderId,
          orderRef: attempt.orderRef,
          failedNames,
        });
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }

      // FULL success — the attempt is complete, so the NEXT create is a new
      // attempt with a new key.
      pendingUploadsRef.current = null;
      createIdem.clear();
      setSubmitSuccess(true);
      setSubmittedRef(attempt.orderRef);
      setSubmittedId(attempt.orderId);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Submit ----
  //
  // A thin, unconditional pass-through to `submitOrder`, which owns the
  // idempotency key, the attempt identity and the owed-uploads ref. It is a
  // wrapper rather than `onSubmit={submitOrder}` because `OrderForm` calls
  // back as `(payload, { items })` while `submitOrder` takes `items` and the
  // department id positionally.
  const handleSubmit = async (payload, { items }) => {
    await submitOrder(payload, items, activeDepartment ? activeDepartment.department_id : null);
  };


  // ---- "Create Another" — reset and remount form ----
  const handleCreateAnother = () => {
    setSubmitSuccess(false);
    setSubmittedRef('');
    setSubmittedId(null);
    setSubmitError('');
    // Re-seed initialOrder with the remembered fields.
    // 🔻 `production_id` came off this list on 2026-08-12 with the auto-pick
    // above. There is nothing to remember: the next order's production is
    // whatever the department it files into says, resolved at render.
    setInitialOrder(prev => {
      const current = prev || {};
      return {
        requested_by: current.requested_by,
        department: current.department,
        buyer_email: current.buyer_email,
        user_phone: current.user_phone,
        sub_department: current.sub_department,
        // MY SPACE "HEADER DETAILS" — carried forward, unlike the lead time.
        // 🔑 THE OPPOSITE DECISION FROM `DEFAULT_NEW_ORDER_LEAD_TIME_DAYS`, and
        // the difference is the owner's own rule for which values travel: a
        // remembered field is a fact about the PERSON and is stable between
        // orders (`sfx_requested_by`, `sfx_buyer_email`), where a lead time is a
        // fact about the PURCHASE and is exactly what varies. An invoice and
        // delivery address is the first kind, and he asked for it in terms —
        // *"this should persist for ALL MySpace orders"*.
        header_details: current.header_details,
      };
    });
    // 🔻 AND RE-READ THE SAVED DEFAULT. The create that just succeeded may have
    // CHANGED it (the server writes `users.myspace_header_details` on every My
    // Space create), so the in-memory copy above can be one order out of date —
    // and "Create Another" never remounts this page, so nothing else would
    // refresh it short of a full reload. The fetch's own guard leaves anything
    // already typed alone.
    loadHeaderDetailsDefault();
    setFormKey(k => k + 1);
  };

  // Site admin cannot create orders directly — render block instead.
  if (isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg p-6 text-amber-900 dark:text-amber-200">
          <h2 className="text-lg font-semibold mb-2">Site admin cannot create orders directly</h2>
          <p className="text-sm mb-3">
            Orders are owned by the user who creates them, in a specific production and department.
            As site admin you are not in any production, so creating an order from this account
            would leave it without a real owner.
          </p>
          <p className="text-sm">
            To create on behalf of a user, use <strong>Impersonate</strong> from the People panel
            to switch to that user's session — then return here to submit the order. Admin
            actions are logged with both identities.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px] mx-auto p-2 max-md:px-[5px] md:p-4 lg:p-6" data-mobile-compact="">
      {leaveGuard.dialog}
      {duplicateOrder && !submitSuccess && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-300 rounded-lg text-indigo-800" data-testid="duplicate-banner">
          <p className="font-semibold">Duplicating from {getOrderNumber(duplicateOrder)}</p>
          <p className="text-sm mt-1">This is a new order pre-filled with data from {getOrderNumber(duplicateOrder)}. A new order number will be assigned on submission.</p>
        </div>
      )}

      {submitSuccess && (
        // Owner 2026-10-03 (phone AND desktop): a quiet confirmation and two
        // ways on — the new order opened in My Orders (the deep link the bell
        // uses; never the retiring /orders/:id page), or another order.
        <div
          className="mb-6 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          data-testid="submit-success"
          role="status"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 shrink-0 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-semibold text-gray-800 dark:text-gray-100">Order sent</p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 ml-7">
            <strong data-testid="submitted-ref" className="text-gray-800 dark:text-gray-100">{submittedRef}</strong> is waiting for approval.
          </p>
          <div className="flex flex-wrap gap-2 mt-4 ml-7">
            <Link
              to={submittedId ? `/orders?order=${submittedId}` : '/orders'}
              className={BTN.primary}
              data-testid="view-my-orders-link"
            >
              View in My Orders
            </Link>
            <button
              type="button"
              onClick={handleCreateAnother}
              className={BTN.secondary}
              data-testid="create-another-order"
            >
              + New order
            </button>
          </div>
        </div>
      )}

      {attachmentWarning && !submitSuccess && (
        // PARTIAL outcome — the order EXISTS; only attachments are owed. This
        // panel must never read as a submit failure (that wording is what
        // minted duplicate orders), and its no-duplicate promise is honest
        // because a resubmit from this state never re-sends the create at all
        // (`pendingUploadsRef`). Same palette as the admin notice above.
        <div
          className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg text-amber-900 dark:text-amber-200"
          data-testid="attachment-warning"
          role="status"
        >
          <p className="font-semibold">
            Order {attachmentWarning.orderRef} was created — {attachmentWarning.failedNames.length} attachment{attachmentWarning.failedNames.length === 1 ? '' : 's'} could not be attached
          </p>
          <ul className="text-sm mt-2 list-disc list-inside" data-testid="attachment-warning-files">
            {attachmentWarning.failedNames.map((name, i) => (
              <li key={`${name}-${i}`}>{name}</li>
            ))}
          </ul>
          <p className="text-sm mt-2">
            Your order is saved and pending approval — only the attachment{attachmentWarning.failedNames.length === 1 ? '' : 's'} listed above {attachmentWarning.failedNames.length === 1 ? 'is' : 'are'} missing.
            If an attachment in the form shows <strong>Retry</strong>, press it (or remove that file) first, then press <strong>Submit Order</strong> again — the order itself will not be resubmitted or duplicated.
            You can also add the file{attachmentWarning.failedNames.length === 1 ? '' : 's'} later from the order's Attachments section.
          </p>
          {attachmentWarning.orderId != null && (
            <Link
              to={`/orders?order=${attachmentWarning.orderId}`}
              className="inline-flex items-center mt-3 text-sm font-medium underline"
              data-testid="attachment-warning-view-order"
            >
              View in My Orders
            </Link>
          )}
        </div>
      )}

      {loadError && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-800">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold">Could not load form data</p>
              <p className="text-sm mt-1">{loadError}</p>
              <button
                type="button"
                onClick={loadFormData}
                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-sm font-medium rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry
              </button>
            </div>
          </div>
        </div>
      )}


      <div className={submitSuccess ? 'hidden md:block' : ''}>
        <OrderForm
          key={formKey}
          mode="create"
          initialOrder={initialOrder}
          productions={productions}
          suppliers={suppliers}
          supplierSuggestions={supplierSuggestions}
          loadFailures={loadFailures}
          gags={gags}
          subDepartments={subDepartments}
          costCodes={costCodes}
          paymentTypes={paymentTypes}
          userAssignedProductions={userAssignedProductions}
          submitting={submitting}
          // 🔒 DUPLICATE-ORDER HAZARD (2026-08-29): the success panel above
          // stays up and the form stays visible on desktop (`hidden md:block`
          // only), with `submitting` back to `false` the moment the request
          // settles — so without this, a deliberate second press re-runs
          // `submitOrder`, takes the `!attempt` branch and mints a FRESH
          // Idempotency-Key, which is a real second `POST /api/orders`. This
          // is narrower than `submitSuccess` looks: the PARTIAL outcome
          // (order created, an attachment upload failed) never sets
          // `submitSuccess` true — see the `attachmentWarning` branch in
          // `submitOrder`, which returns before it — so Submit stays armed
          // there, which is required: the amber panel's own wording tells the
          // user to press Submit again to retry the missing claims, and this
          // must not disable the button that promise depends on.
          // `handleCreateAnother` clears `submitSuccess`, which re-arms this
          // for free — no separate reset needed.
          submitDisabled={submitSuccess}
          submitDisabledHint={submitSuccess ? 'Order already submitted — use “+ New order” to raise a new one' : null}
          submitError={submitError}
          submitLabel="Submit Order"
          onSubmit={handleSubmit}
          onDirtyChange={setFormDirty}
          showItemFileUpload
          showOrderFileUpload
          persistFieldsToLocalStorage
          pendingFiles={pending}
          pendingFilesDisabled={submitting}
        />
      </div>
    </div>
  );
}

export default NewOrder;
